import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle, Clock, WarningCircle, Airplane, ChartPieSlice, UsersThree, PencilSimpleLine } from "@phosphor-icons/react";

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
};

const dayLabel = (d) => {
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-US", { weekday: "short", day: "numeric", month: "short" });
};

const CHIP = {
  up_to_date: { label: "Up to date", cls: "bg-rag-green-bg text-rag-green-fg border-rag-green/30", Icon: CheckCircle },
  pending: { label: "Pending approval", cls: "bg-rag-amber-bg text-rag-amber-fg border-rag-amber/30", Icon: Clock },
  missing: { label: "Missing days", cls: "bg-rag-red-bg text-rag-red-fg border-rag-red/30", Icon: WarningCircle },
};

const StatusChip = ({ status }) => {
  const c = CHIP[status] || CHIP.up_to_date;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 border rounded-sm ${c.cls}`} data-testid={`chip-${status}`}>
      <c.Icon size={11} weight="fill" /> {c.label}
    </span>
  );
};

const MonthCard = ({ title, s, testid, showMissing }) => (
  <Card className="p-5 border-border rounded-sm" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <div className="text-[11px] font-mono uppercase tracking-widest text-primary">{title}</div>
      <div className="text-[11px] font-mono text-muted-foreground">{monthLabel(s.month)}</div>
    </div>
    <div className="grid grid-cols-2 gap-4 mb-4">
      <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Total Hours</div><div className="font-display text-3xl text-foreground">{s.total_hours}h</div></div>
      <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Late Submissions</div><div className={`font-display text-3xl ${s.late_count ? "text-rag-red-fg" : "text-foreground"}`}>{s.late_count}</div></div>
    </div>
    <div className="grid grid-cols-5 gap-2 mb-4 text-center">
      <div className="border border-border/60 rounded-sm py-2"><div className="font-display text-xl text-rag-green-fg">{s.days_approved}</div><div className="text-[9px] font-mono uppercase text-muted-foreground">Approved</div></div>
      <div className="border border-border/60 rounded-sm py-2"><div className="font-display text-xl text-rag-amber-fg">{s.days_pending}</div><div className="text-[9px] font-mono uppercase text-muted-foreground">Pending</div></div>
      <div className="border border-border/60 rounded-sm py-2"><div className={`font-display text-xl ${s.days_missing ? "text-rag-red-fg" : "text-muted-foreground"}`}>{s.days_missing}</div><div className="text-[9px] font-mono uppercase text-muted-foreground">Missing</div></div>
      <div className="border border-border/60 rounded-sm py-2"><div className="font-display text-xl text-muted-foreground">{s.days_leave}</div><div className="text-[9px] font-mono uppercase text-muted-foreground">Leave</div></div>
      <div className="border border-border/60 rounded-sm py-2" data-testid="month-holiday"><div className="font-display text-xl text-sky-500">{s.days_holiday || 0}</div><div className="text-[9px] font-mono uppercase text-muted-foreground">Holiday</div></div>
    </div>
    <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Hours by status</div>
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
      <span className="text-muted-foreground">Draft <b className="text-foreground">{s.by_status.draft}h</b></span>
      <span className="text-muted-foreground">Submitted <b className="text-rag-amber-fg">{s.by_status.submitted}h</b></span>
      <span className="text-muted-foreground">Approved <b className="text-rag-green-fg">{s.by_status.approved}h</b></span>
      <span className="text-muted-foreground">Rejected <b className="text-rag-red-fg">{s.by_status.rejected}h</b></span>
    </div>
    <div className="mt-3 text-[11px] font-mono text-muted-foreground">
      Submitted <b className="text-foreground">{s.days_submitted}</b> / {s.days_expected} working days
    </div>
    {showMissing && (
      <div className="mt-4 pt-4 border-t border-border/60" data-testid="my-missing-days">
        <div className="flex items-center gap-1.5 mb-2">
          <WarningCircle size={12} className={s.missing_dates?.length ? "text-rag-red-fg" : "text-rag-green-fg"} weight="fill" />
          <span className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">Days you haven't submitted{s.missing_dates?.length ? ` (${s.missing_dates.length})` : ""}</span>
        </div>
        {s.missing_dates?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {s.missing_dates.map(d => (
              <Link key={d} to={`/timesheet?date=${d}`} data-testid={`missing-day-${d}`}
                className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-sm border border-rag-red/30 bg-rag-red-bg text-rag-red-fg hover:bg-rag-red-bg transition-colors">
                <PencilSimpleLine size={11} /> {dayLabel(d)}
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-[11px] text-rag-green-fg">You're all caught up this month. 🎉</div>
        )}
      </div>
    )}
  </Card>
);

const MemberRow = ({ r }) => {
  const c = r.current, p = r.previous;
  return (
    <tr className="border-b border-border/60 hover:bg-muted/20" data-testid={`team-row-${r.user_id}`}>
      <td className="px-4 py-3">
        <div className="font-medium text-foreground">{r.name}</div>
        <div className="text-[11px] font-mono text-muted-foreground">{r.email}</div>
      </td>
      <td className="px-4 py-3"><StatusChip status={r.status} /></td>
      <td className="px-4 py-3 text-center font-mono">{c.total_hours}h</td>
      <td className="px-4 py-3 text-center font-mono text-rag-green-fg">{c.days_submitted}<span className="text-muted-foreground">/{c.days_expected}</span></td>
      <td className="px-4 py-3 text-center font-mono text-rag-amber-fg">{c.days_pending}</td>
      <td className={`px-4 py-3 text-center font-mono ${c.days_missing ? "text-rag-red-fg" : "text-muted-foreground"}`}>{c.days_missing}</td>
      <td className="px-4 py-3 text-center font-mono text-muted-foreground">{c.days_leave}</td>
      <td className="px-4 py-3 text-center font-mono text-sky-500" data-testid={`team-holiday-${r.user_id}`}>{c.days_holiday || 0}</td>
      <td className={`px-4 py-3 text-center font-mono ${c.late_count ? "text-rag-red-fg" : "text-muted-foreground"}`}>{c.late_count}</td>
      <td className="px-4 py-3 text-center font-mono text-muted-foreground">{p.total_hours}h · {p.days_submitted}/{p.days_expected}d</td>
    </tr>
  );
};

export default function TimesheetDashboard() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || ["manager", "admin"].includes(user?.timesheet_role);
  const [me, setMe] = useState(null);
  const [team, setTeam] = useState(null);

  const load = useCallback(() => {
    api.get("/timesheet/dashboard/me").then(r => setMe(r.data)).catch(() => toast.error("Failed to load your summary"));
    if (isManager) api.get("/timesheet/dashboard/team").then(r => setTeam(r.data)).catch(() => {});
  }, [isManager]);

  useEffect(() => { load(); }, [load]);

  const grouped = team?.grouped_by_manager
    ? team.rows.reduce((acc, r) => { (acc[r.manager_name] = acc[r.manager_name] || []).push(r); return acc; }, {})
    : null;

  return (
    <div className="p-6 lg:p-8 space-y-8 max-w-7xl" data-testid="ts-dashboard-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Timesheet</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black">Dashboard</h1>
      </div>

      {/* My summary */}
      <section data-testid="ts-my-summary">
        <div className="flex items-center gap-2 mb-4"><ChartPieSlice size={16} className="text-primary" /><span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">My Summary</span></div>
        {me ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <MonthCard title="This Month" s={me.current} testid="me-current" showMissing />
            <MonthCard title="Previous Month" s={me.previous} testid="me-previous" />
          </div>
        ) : <div className="text-sm text-muted-foreground">Loading…</div>}
      </section>

      {/* Team summary */}
      {isManager && (
        <section data-testid="ts-team-summary">
          <div className="flex items-center gap-2 mb-4"><UsersThree size={16} className="text-primary" /><span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Team Summary {team && <span className="text-muted-foreground/60">· {monthLabel(team.months.current)}</span>}</span></div>

          {team && <ChaseList rows={team.rows} month={team.months.current} />}

          {team && team.rows.length === 0 && <div className="text-sm text-muted-foreground">No team members report to you yet.</div>}
          {team && team.rows.length > 0 && (
            grouped ? (
              Object.entries(grouped).map(([mgr, rows]) => (
                <div key={mgr} className="mb-6" data-testid={`mgr-group-${mgr}`}>
                  <div className="text-xs font-mono uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2"><Airplane size={12} className="opacity-0" />Reports to <span className="text-foreground">{mgr}</span> <span className="text-muted-foreground/60">({rows.length})</span></div>
                  <TeamTable rows={rows} />
                </div>
              ))
            ) : <TeamTable rows={team.rows} />
          )}
        </section>
      )}
    </div>
  );
}

const ChaseList = ({ rows, month }) => {
  const owing = rows
    .map(r => ({ ...r, missing: r.current?.missing_dates || [] }))
    .filter(r => r.missing.length > 0)
    .sort((a, b) => b.missing.length - a.missing.length);
  return (
    <Card className="border-border rounded-sm mb-6 overflow-hidden" data-testid="ts-chase-list">
      <div className="p-4 border-b border-border flex items-center gap-2">
        <WarningCircle size={14} className={owing.length ? "text-rag-red-fg" : "text-rag-green-fg"} weight="fill" />
        <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Missing submissions · {monthLabel(month)}</span>
        <span className="ml-auto text-[11px] font-mono text-muted-foreground">{owing.length} member{owing.length === 1 ? "" : "s"} owing</span>
      </div>
      {owing.length === 0 ? (
        <div className="p-4 text-sm text-rag-green-fg">Everyone is up to date this month. 🎉</div>
      ) : (
        <div className="divide-y divide-border/60">
          {owing.map(r => (
            <div key={r.user_id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`chase-row-${r.user_id}`}>
              <div className="sm:w-56 shrink-0">
                <div className="font-medium text-foreground text-sm">{r.name}</div>
                <div className="text-[11px] font-mono text-muted-foreground">{r.missing.length} missing day{r.missing.length === 1 ? "" : "s"}</div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {r.missing.map(d => (
                  <span key={d} data-testid={`chase-${r.user_id}-${d}`}
                    className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-sm border border-rag-red/30 bg-rag-red-bg text-rag-red-fg">
                    {dayLabel(d)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const TeamTable = ({ rows }) => (
  <Card className="border-border rounded-sm overflow-hidden" data-testid="ts-team-table">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
          <th className="text-left px-4 py-3">Member</th>
          <th className="text-left px-4 py-3">Status</th>
          <th className="text-center px-4 py-3">Hours</th>
          <th className="text-center px-4 py-3">Submitted</th>
          <th className="text-center px-4 py-3">Pending</th>
          <th className="text-center px-4 py-3">Missing</th>
          <th className="text-center px-4 py-3">Leave</th>
          <th className="text-center px-4 py-3">Holiday</th>
          <th className="text-center px-4 py-3">Late</th>
          <th className="text-center px-4 py-3">Prev Month</th>
        </tr></thead>
        <tbody>{rows.map(r => <MemberRow key={r.user_id} r={r} />)}</tbody>
      </table>
    </div>
  </Card>
);
